import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowLeft, ListOrdered } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../components/ui/tabs';
import {
  pipefitterRandomCompatReset,
  pipefitterRandomCompatSubmit,
} from '../../../../redux/Pipefitter/actions';
import type { DashboardProject } from '../../../project/types/project';
import { PIPEFITTER_TAB_TRIGGER_CLASS } from '../../constants/uiConstants';
import { formatSeamSeparationMm, formatFitDataUpdatedAt } from './helpers/formatReport';
import { SetupView, type SetupPipeDefaults } from './views/SetupView';
import { ProgressView } from './views/ProgressView';
import { OverviewView } from './views/OverviewView';
import { PerPipeView } from './views/PerPipeView';
import { HandlingView } from './views/HandlingView';
import { TurningView } from './views/TurningView';
import { ShortlistView } from './views/ShortlistView';
import { SinglePassView } from './views/SinglePassView';
import type { CompatReportDocument } from './types';
import {
  EMPTY_ARRIVAL_ORDER_STATE,
  type ArrivalOrderState,
} from './views/ArrivalOrderPanel';
import { GenerateStakeholderReportButton } from './report/GenerateStakeholderReportButton';
import { GenerateSequenceFromCompatModal } from './GenerateSequenceFromCompatModal';
import { cn } from '../../../../lib/utils';

interface RandomPipeCompatibilityModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: DashboardProject;
  selectedPipe?: SetupPipeDefaults | null;
}

type ReportTab =
  | 'setup'
  | 'progress'
  | 'overview'
  | 'pipes'
  | 'handling'
  | 'flipping'
  | 'shortlist'
  | 'singlepass';

export const RandomPipeCompatibilityModal: React.FC<RandomPipeCompatibilityModalProps> = ({
  open,
  onOpenChange,
  project,
  selectedPipe,
}) => {
  const dispatch = useDispatch();
  const projectNumber = project?.project?.projectNumber ?? '';
  const projectName = project?.project?.projectName ?? '';

  const compat = useSelector((state: any) => state.pipefitterRandomCompat || {});
  const phase: string = compat.phase || 'idle';
  const progress = compat.progress ?? null;
  const lotProgress = compat.lotProgress ?? null;
  const withoutThemProgress = compat.withoutThemProgress ?? null;
  const withoutThemSeen = Boolean(compat.withoutThemSeen);
  const estimatedRuns = compat.estimatedRuns ?? null;
  const result: CompatReportDocument | null = compat.result ?? null;
  const reason: string | null = compat.reason || compat.error || null;
  const lastRequest = compat.lastRequest ?? null;
  const jobId: string | null = compat.jobId ?? null;

  const pipeStatusDataRaw = useSelector((state: any) => state.pipefitterReports?.pipeStatusData);
  const pipeStatusRows = Array.isArray(pipeStatusDataRaw)
    ? pipeStatusDataRaw
    : pipeStatusDataRaw
      ? [pipeStatusDataRaw]
      : [];
  const pipeStatusLoading = Boolean(
    useSelector(
      (state: any) =>
        state.pipefitterReports?.isReportsLoading || state.pipefitterReports?.pipeStatusLoadingMore
    )
  );

  const [tab, setTab] = useState<ReportTab>('setup');
  const [arrivalOrder, setArrivalOrder] = useState<ArrivalOrderState>(EMPTY_ARRIVAL_ORDER_STATE);
  /** Bump when the modal closes so Setup remounts fresh next open — not on tab switches. */
  const [setupKey, setSetupKey] = useState(0);
  const [generateSequenceOpen, setGenerateSequenceOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      dispatch(pipefitterRandomCompatReset());
      setTab('setup');
      setArrivalOrder(EMPTY_ARRIVAL_ORDER_STATE);
      setSetupKey((k) => k + 1);
      setGenerateSequenceOpen(false);
    }
  }, [open, dispatch]);

  // Clear sequence only when the report/job id changes (new run), not on tab switches.
  useEffect(() => {
    setArrivalOrder(EMPTY_ARRIVAL_ORDER_STATE);
  }, [jobId]);

  useEffect(() => {
    if (phase === 'submitting' || phase === 'working') {
      setTab('progress');
    } else if (phase === 'done' && result) {
      setTab('overview');
    }
  }, [phase, result]);

  const metaLine = useMemo(() => {
    if (!result) return null;
    const s = result.settings;
    const seam = formatSeamSeparationMm(s.weldSeamSeparationMm, s.weldSeamExclusionDeg);
    const fitUpdated = formatFitDataUpdatedAt(result.project.fitDataUpdatedAt);
    return (
      <>
        Batch <span className="font-semibold text-text-grey">{result.project.pipeCount} pipes</span>
        {' · '}
        {result.project.reportDate}
        {' · '}
        hi-lo <span className="font-semibold text-text-grey">{s.hiLoThresholdMm} mm</span>
        {' · '}
        seam <span className="font-semibold text-text-grey">{seam}</span>
        {' · '}
        rotation <span className="font-semibold text-text-grey">{s.rotation || '—'}</span>
        {' · '}
        turning{' '}
        <span className="font-semibold text-text-grey">
          {s.flippingAllowed ? 'allowed' : 'off'}
        </span>
        {fitUpdated ? (
          <>
            {' · '}
            fit data updated <span className="font-semibold text-text-grey">{fitUpdated}</span>
          </>
        ) : null}
      </>
    );
  }, [result]);

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      dispatch(pipefitterRandomCompatReset());
      setTab('setup');
    }
    onOpenChange(nextOpen);
  };

  const handleRun = (body: Record<string, unknown>) => {
    dispatch(pipefitterRandomCompatSubmit(body));
  };

  const handleRetry = () => {
    if (lastRequest) {
      dispatch(pipefitterRandomCompatSubmit(lastRequest));
    } else {
      setTab('setup');
      dispatch(pipefitterRandomCompatReset());
    }
  };

  const reportReady = phase === 'done' && Boolean(result);
  const showProgress = phase === 'submitting' || phase === 'working' || phase === 'failed';

  const shortlistRequested = Boolean(
    result?.shortlist ||
      (lastRequest &&
        (lastRequest.pipesNeeded != null || lastRequest.pipesToDeliver != null))
  );
  const turningAllowed =
    result?.settings?.flippingAllowed === true ||
    lastRequest?.flippingAllowed === true;

  const showShortlistTab = reportReady && shortlistRequested;
  const showTurningTab = reportReady && turningAllowed;

  // Leave hidden tabs if setup choices make them unavailable.
  useEffect(() => {
    if (tab === 'shortlist' && !showShortlistTab) setTab(reportReady ? 'overview' : 'setup');
    if (tab === 'flipping' && !showTurningTab) setTab(reportReady ? 'overview' : 'setup');
  }, [tab, showShortlistTab, showTurningTab, reportReady]);

  return (
    <>
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-screen h-screen max-w-none max-h-[100dvh] m-0 p-0 gap-0 border-0 bg-page-bg shadow-none rounded-none [&>button]:hidden flex flex-col overflow-hidden">
        <DialogHeader className="px-4 sm:px-6 py-4 border-b border-[#E2E5EA] flex-shrink-0 bg-white">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              type="button"
              onClick={() => handleClose(false)}
              className="shrink-0 h-9 w-9 p-0 border-[#E2E5EA] hover:bg-page-bg"
              variant="outline"
              size="icon"
              aria-label="Close random pipe compatibility"
            >
              <ArrowLeft className="h-4 w-4 text-text-grey" />
            </Button>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-heritage-grey mb-0.5">
                Random pipe compatibility
              </p>
              <DialogTitle className="font-heading text-[18px] sm:text-[22px] font-bold text-blueprint-blue leading-tight">
                {result ? `Project ${result.project.number}` : `Project ${projectNumber || '—'}`}
              </DialogTitle>
              {metaLine ? (
                <p className="text-[12.5px] text-heritage-grey mt-1 truncate">{metaLine}</p>
              ) : projectName ? (
                <p className="text-[12.5px] text-heritage-grey mt-1 truncate">{projectName}</p>
              ) : null}
            </div>
            {reportReady && result && jobId && (
              <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                <Button
                  type="button"
                  className="h-9 shrink-0 bg-hero-red hover:bg-hero-red/90 text-white"
                  onClick={() => setGenerateSequenceOpen(true)}
                >
                  <ListOrdered className="h-4 w-4" />
                  <span className="ml-2">Generate sequence</span>
                </Button>
                <GenerateStakeholderReportButton reportId={jobId} className="shrink-0" />
              </div>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="max-w-full   mx-auto px-4 sm:px-6 py-5 pb-16">
            <Tabs
              value={tab}
              onValueChange={(v) => setTab(v as ReportTab)}
              className="w-full"
            >
              <TabsList className="h-auto w-full justify-start gap-4 bg-transparent px-0 py-0 border-b border-[#E2E5EA] rounded-none mb-5 flex-wrap md:gap-6 lg:gap-8">
                {(
                  [
                    { key: 'setup', label: 'Set up', enabled: true, visible: true },
                    {
                      key: 'progress',
                      label: 'Progress',
                      enabled: showProgress || reportReady,
                      visible: true,
                    },
                    { key: 'overview', label: 'Overview', enabled: reportReady, visible: true },
                    { key: 'pipes', label: 'Per pipe', enabled: reportReady, visible: true },
                    { key: 'handling', label: 'Handling', enabled: reportReady, visible: true },
                    {
                      key: 'flipping',
                      label: 'Turning',
                      enabled: showTurningTab,
                      visible: showTurningTab,
                    },
                    {
                      key: 'shortlist',
                      label: 'Shortlist',
                      enabled: showShortlistTab,
                      visible: showShortlistTab,
                    },
                    {
                      key: 'singlepass',
                      label: 'Single pass',
                      enabled: reportReady,
                      visible: true,
                    },
                  ] as const
                )
                  .filter((t) => t.visible)
                  .map((t) => (
                  <TabsTrigger
                    key={t.key}
                    value={t.key}
                    disabled={!t.enabled}
                    className={cn(
                      PIPEFITTER_TAB_TRIGGER_CLASS,
                      'shrink-0 px-2 disabled:opacity-40 disabled:pointer-events-none'
                    )}
                  >
                    {t.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              <TabsContent
                value="setup"
                forceMount
                className="mt-0 data-[state=inactive]:hidden"
              >
                <SetupView
                  key={setupKey}
                  projectNumber={projectNumber}
                  selectedPipe={selectedPipe}
                  isSubmitting={phase === 'submitting'}
                  onRun={handleRun}
                  pipeStatusRows={pipeStatusRows}
                  pipeStatusLoading={pipeStatusLoading}
                />
              </TabsContent>

              <TabsContent value="progress" className="mt-0">
                {phase === 'failed' ? (
                  <div className="space-y-4">
                    <div className="rounded-xl border border-[rgba(235,28,55,0.25)] bg-[rgba(235,28,55,0.06)] p-5">
                      <p className="text-[11px] font-semibold tracking-wider uppercase text-hero-red mb-2">
                        Failed
                      </p>
                      <p className="text-[14px] text-text-grey">{reason || 'Report failed'}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button type="button" onClick={handleRetry} className="bg-hero-red hover:bg-hero-red/90">
                        Retry
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          dispatch(pipefitterRandomCompatReset());
                          setTab('setup');
                        }}
                      >
                        Back to set up
                      </Button>
                    </div>
                  </div>
                ) : (
                  <ProgressView
                    progress={progress}
                    lotProgress={lotProgress}
                    withoutThemProgress={withoutThemProgress}
                    estimatedRuns={estimatedRuns}
                    phase={phase}
                    result={result}
                    withoutThemSeen={withoutThemSeen}
                    lastRequest={lastRequest}
                    shortlistRequested={shortlistRequested}
                  />
                )}
              </TabsContent>

              <TabsContent value="overview" className="mt-0">
                {result && (
                  <OverviewView
                    result={result}
                    reportId={jobId}
                    arrivalOrder={arrivalOrder}
                    onArrivalOrderChange={setArrivalOrder}
                  />
                )}
              </TabsContent>
              <TabsContent value="pipes" className="mt-0">
                {result && <PerPipeView result={result} />}
              </TabsContent>
              <TabsContent value="handling" className="mt-0">
                {result && <HandlingView result={result} />}
              </TabsContent>
              <TabsContent value="flipping" className="mt-0">
                {result && <TurningView result={result} />}
              </TabsContent>
              <TabsContent value="shortlist" className="mt-0">
                {result && <ShortlistView result={result} />}
              </TabsContent>
              <TabsContent value="singlepass" className="mt-0">
                {result && <SinglePassView result={result} />}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </DialogContent>
    </Dialog>

      {reportReady && result && (
        <GenerateSequenceFromCompatModal
          open={generateSequenceOpen}
          onOpenChange={setGenerateSequenceOpen}
          project={project}
          result={result}
          selectedPipe={selectedPipe}
        />
      )}
    </>
  );
};
